/**
 * useDelivery — calls the Uber Direct Cloud Functions
 *
 * Three callables:
 *   estimateDeliveryFee({ businessId, deliveryAddress }) → { fee, eta }
 *   getDeliveryQuote({ orderId })  → { quoteId, fee, currency, dropoffEta }
 *   checkDeliveryStatus({ orderId }) → { deliveryId, status, trackingUrl, courier, ... }
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// ─── Types ──────────────────────────────────────────────────

export interface DeliveryEstimate {
  fee: number;       // dollars
  /** ISO 8601 dropoff estimate — format with `formatEtaAsWindow` before display. */
  eta: string | null;
  /** ISO 8601 quote expiry. Re-quote past this or the ETA is stale. */
  expiresAt?: string | null;
  estimated?: boolean;
}

export interface DeliveryQuote {
  quoteId: string;
  fee: number;       // smallest currency unit (cents)
  currency: string;
  dropoffEta: string | null;
}

export interface DeliveryStatus {
  deliveryId: string;
  status: string;
  trackingUrl?: string;
  courier?: {
    name?: string;
    phone_number?: string;
    vehicle_type?: string;
    img_href?: string;
    location?: { lat: number; lng: number };
  };
  pickupEta?: string;
  dropoffEta?: string;
}

interface DeliveryAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

// ─── Hook ───────────────────────────────────────────────────

export function useDelivery() {
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Pre-checkout estimate — no orderId needed */
  const estimateFee = useCallback(async (
    businessId: string,
    deliveryAddress: DeliveryAddress
  ): Promise<DeliveryEstimate | null> => {
    setEstimateLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { businessId: string; deliveryAddress: DeliveryAddress },
        DeliveryEstimate & { error?: string }
      >(functions, 'estimateDeliveryFee');
      const result = await fn({ businessId, deliveryAddress });

      if (result.data.error) {
        setError(result.data.error);
        return null;
      }

      return result.data;
    } catch (err: any) {
      console.error('estimateDeliveryFee error:', err);
      setError(err.message || 'Failed to estimate delivery fee');
      return null;
    } finally {
      setEstimateLoading(false);
    }
  }, []);

  /** Get an Uber delivery quote (fee + ETA) for an order */
  const getQuote = useCallback(async (orderId: string): Promise<DeliveryQuote | null> => {
    setQuoteLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ orderId: string }, DeliveryQuote>(functions, 'getDeliveryQuote');
      const result = await fn({ orderId });
      return result.data;
    } catch (err: any) {
      console.error('getDeliveryQuote error:', err);
      setError(err.message || 'Failed to get delivery quote');
      return null;
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  /** Drop a stale error, e.g. once the shopper starts editing their address again */
  const clearError = useCallback(() => setError(null), []);

  /** Poll Uber for current delivery status (fallback if webhook missed) */
  const checkStatus = useCallback(async (orderId: string): Promise<DeliveryStatus | null> => {
    setStatusLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ orderId: string }, DeliveryStatus>(functions, 'checkDeliveryStatus');
      const result = await fn({ orderId });
      return result.data;
    } catch (err: any) {
      console.error('checkDeliveryStatus error:', err);
      setError(err.message || 'Failed to check delivery status');
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, []);

  return {
    estimateFee,
    getQuote,
    checkStatus,
    clearError,
    estimateLoading,
    quoteLoading,
    statusLoading,
    error,
  };
}
